import {compareVersions} from './appVersionsService';
import {
    ALL_PLATFORMS,
    AppPlatform,
    AppVersions,
    PLATFORM_DISPLAY_NAMES,
    PLATFORM_MAP,
} from '../types/feedback';

export interface ShipmentCheck {
    /** `shipped` : la version corrective est publiée partout où le bug frappe —
     *  sur la plateforme identifiée, ou sur toutes si le bug n'en nomme aucune.
     *  `partial` : publiée sur une partie seulement — la moitié des utilisateurs
     *  attend encore, et le dire évite de prévenir les signalants trop tôt.
     *  `pending` : publiée nulle part.
     *  `unknown` : rien de comparable. */
    state: 'shipped' | 'partial' | 'pending' | 'unknown';
    /** Libellés des plateformes où le correctif est en production. */
    live: string[];
    /** Libellés des plateformes où il ne l'est pas encore. */
    waiting: string[];
    /** Plateformes dont la version publiée est inconnue. Elles empêchent de
     *  conclure « livré » : ne pas savoir n'est pas savoir que oui. */
    unknown: string[];
}

const NOTHING_COMPARABLE: ShipmentCheck = {
    state: 'unknown', live: [], waiting: [], unknown: [],
};

/**
 * Le correctif est-il arrivé chez les utilisateurs ?
 *
 * Répond en comparant la version de livraison aux versions publiées, plateforme
 * par plateforme : un correctif livré en 5.3.2 sur iOS mais pas sur Android ne
 * corrige rien pour la moitié des signalants.
 *
 * `web` est écarté : sa « version » est une date, elle ne se compare pas à un
 * triplet sémantique. Un bug qui ne touche que le web reste donc indéterminé —
 * ce qui est plus honnête qu'une comparaison inventée.
 */
export function checkShipment(
    fixVersion: string | undefined,
    platforms: string[] | undefined,
    versions: AppVersions | null,
    storeVersions: Record<string, string> = {},
): ShipmentCheck {
    if (!fixVersion || !/^\d/.test(fixVersion.trim())) return NOTHING_COMPARABLE;

    const scoped = (platforms ?? [])
        .map((p) => PLATFORM_MAP[p] ?? PLATFORM_MAP[p.toLowerCase()])
        .filter((p): p is AppPlatform => Boolean(p));
    // Portée inconnue : on compare partout. Mieux vaut annoncer « pas encore
    // livré sur Windows » que de déclarer livré sur la foi d'une seule store.
    const considered = (scoped.length > 0 ? scoped : ALL_PLATFORMS)
        .filter((p) => p !== 'web');

    const live: string[] = [];
    const waiting: string[] = [];
    const unknown: string[] = [];
    for (const platform of considered) {
        const inProd = storeVersions[platform] || versions?.[platform];
        const label = PLATFORM_DISPLAY_NAMES[platform];
        if (!inProd) unknown.push(label);
        else if (compareVersions(inProd, fixVersion) >= 0) live.push(label);
        else waiting.push(label);
    }

    if (live.length === 0 && waiting.length === 0) return NOTHING_COMPARABLE;
    // « Livré » demande que TOUTES les plateformes concernées l'aient publiée :
    // une seule inconnue ou en retard suffit a laisser le bug ouvert.
    if (waiting.length === 0 && unknown.length === 0) {
        return {state: 'shipped', live, waiting, unknown};
    }
    if (live.length === 0 && unknown.length === 0) {
        return {state: 'pending', live, waiting, unknown};
    }
    return {state: 'partial', live, waiting, unknown};
}
